import React, { useRef, useState, useEffect, forwardRef, useCallback } from "react";
import classNames from 'classnames';
import styles from './List.less';
import { without, whereEq, prop, propEq, __, includes, identity, startsWith, update, move, isNotNil } from 'ramda';
import { FileState, SortOrder, FileAttrsAttr } from '../../shared/types';
import { filter } from 'rxjs/operators';
import { depth, dirname, parse, childOf, join } from '../../utils/path';
import { useSet, useSubscribe, useEvent, useType } from '../../hooks';
import setRef from '../../ui/setRef';
import { CommandID } from '../../commands';
import { command$ } from '../../observables/command';
import EditFileName from '../EditFileName/EditFileName';
import debounce from '../../utils/debounce';
import { Tooltips } from '../../ui/components';
export var ColumnType;
(function (ColumnType) {
    ColumnType["String"] = "string";
    ColumnType["Number"] = "number";
})(ColumnType || (ColumnType = {}));
export var DropEffect;
(function (DropEffect) {
    DropEffect["Copy"] = "copy";
    DropEffect["Move"] = "move";
})(DropEffect || (DropEffect = {}));
const isDescendant = (path, ancestor) => path.startsWith(ancestor + '/') || path == ancestor;
const dirOf = (item, all) => item.dir ? item.path : all.find(({ path }) => path == dirname(item.path))?.path;
const selectChildren = (parent, list) => list.filter(startsWith(parent + '/'))
    .map(path => path.substring(parent.length + 1))
    .map(path => parse(path)['top']);
export default forwardRef(function List({ columns, files, onGo, onToggle, onSelect, onOpen, onDragStart, onDragEnd, onDragOver, onDrop, onMenu, onNew, onRename, onSort, onColumnsMenu, onColumnsChange, root, tabindex, parent, children }, fwdRef) {
    const rootEl = useRef(null);
    const [items, setItems] = useState([]);
    const [expanded, setExpanded] = useState([]);
    const [rootDepth, setRootDepth] = useState(0);
    const [selected, { has: isSelected, reset: setSelected, toggle: toggleSelected }] = useSet([]);
    const [target, setTarget] = useState();
    const isActive = useRef(false);
    const waitForSecondClick = useRef(undefined);
    const [creating, createIn] = useState();
    const [renaming, rename] = useState();
    const [dropTarget, setDropTarget] = useState('');
    const insertNewItem = (items) => {
        if (creating) {
            let i = items.findIndex(({ path }) => path == creating.in);
            if (i < 0 && creating.in == root) {
                i = 0;
            }
            if (i < 0) {
                return items;
            }
            const newItem = {
                URI: '',
                path: items[i]?.path ?? join(root, 'new'),
                name: '',
                dir: creating.dir,
                size: 0,
                modified: new Date(),
                FileStateAttr: FileState.Creating
            };
            items.splice(creating.in == root ? 0 : i + 1, 0, newItem);
            return [...items];
        }
        return items;
    };
    const setRenamingItem = (items) => {
        if (renaming) {
            const item = items.find(whereEq({ URI: renaming }));
            item && (item.FileStateAttr = FileState.Renaming);
            return [...items];
        }
        return items;
    };
    useEffect(() => {
        if (target && !files.find(whereEq({ URI: target.URI }))) {
            setTarget(undefined);
        }
        setItems(setRenamingItem(insertNewItem(files)));
    }, [files]);
    useEffect(() => {
        setItems(items => {
            items = items.filter(({ FileStateAttr }) => FileStateAttr != FileState.Creating);
            if (creating && (expanded.includes(creating.in) || creating.in == root)) {
                return insertNewItem(items);
            }
            return items;
        });
    }, [creating]);
    useEffect(() => {
        setItems(items => items.map(item => ({
            ...item,
            FileStateAttr: item.URI == renaming
                ? FileState.Renaming
                : undefined
        })));
    }, [renaming]);
    useEffect(() => {
        setSelected([]);
        setExpanded([]);
        if (rootEl.current) {
            rootEl.current.scrollTop = rootEl.current.scrollLeft = 0;
        }
    }, [parent]);
    useEffect(() => {
        const first = items?.[0];
        setRootDepth(first ? depth(first.path) : 0);
    }, [items]);
    useEffect(() => {
        onSelect(selected, target?.path);
    }, [selected, target]);
    useEffect(() => setSelected(selected.filter(includes(__, items.map(prop('path'))))), [items]);
    const [widths, setWidths] = useState([]);
    useEffect(() => {
        setWidths(columns.map(prop('width')));
    }, [columns]);
    const toggle = (dir) => {
        setExpanded(expanded.includes(dir) ? without([dir], expanded) : [...expanded, dir]);
        onToggle(dir);
    };
    const click = (item, meta, shift, expand) => {
        select(item.path, meta, shift);
        setTarget(item);
        clearTimeout(waitForSecondClick.current);
        waitForSecondClick.current = setTimeout(() => {
            if (item.dir && !meta && !shift && expand) {
                toggle(item.path);
            }
        }, 400);
    };
    useEffect(() => {
        if (target && rootEl.current) {
            const targetEl = rootEl.current.querySelector(`[data-path='${target.path}']`);
            if (targetEl) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                const { top: snapTop } = rootEl.current.getBoundingClientRect();
                const { top } = targetEl.getBoundingClientRect();
                if (top <= snapTop) {
                    rootEl.current.scroll?.({ top: 0, behavior: 'instant' });
                }
            }
        }
    }, [target]);
    const select = (path, meta, shift) => {
        if (shift && selected.length) {
            if (isSelected(path)) {
                // TODO remove selection ?
            }
            else {
                const targetIndex = items.findIndex(propEq(path, 'path'));
                const distances = selected.map(path => Math.abs(targetIndex - items.findIndex(propEq(path, 'path'))));
                const closestIndex = items.findIndex(whereEq({ path: selected[distances.indexOf(Math.min(...distances))] }));
                (targetIndex > closestIndex ?
                    items.slice(closestIndex + 1, targetIndex + 1) :
                    items.slice(targetIndex, closestIndex)).forEach(({ path }) => toggleSelected(path));
            }
        }
        else if (meta) {
            toggleSelected(path);
        }
        else {
            setSelected([path]);
        }
    };
    const doubleClick = (item) => {
        clearTimeout(waitForSecondClick.current);
        item.dir ? onGo(item.path) : onOpen(item.path);
    };
    useSubscribe(() => command$.pipe(filter(() => isActive.current)).subscribe(cmd => {
        switch (cmd.id) {
            case CommandID.SelectAll: {
                setSelected(items.map(prop('path')));
                cmd.e?.preventDefault();
                cmd.e?.stopPropagation();
                break;
            }
            case CommandID.SelectAllFiles: {
                setSelected(items.filter(({ dir }) => !dir).map(prop('path')));
                cmd.e?.preventDefault();
                cmd.e?.stopPropagation();
                break;
            }
            case CommandID.CollapseAll: {
                expanded.forEach(onToggle);
                setExpanded([]);
                break;
            }
            case CommandID.NewDir:
            case CommandID.NewFile: {
                const inDir = target ? (dirOf(target, items) || root) : root;
                if (inDir) {
                    !expanded.includes(inDir) && inDir != root && toggle(inDir);
                    createIn({ in: inDir, dir: cmd.id == CommandID.NewDir });
                }
                break;
            }
            case CommandID.Rename: {
                target && rename(cmd.uri ?? target.URI);
                break;
            }
            case CommandID.GoUp: {
                isActive.current && parent && onGo(parent);
                break;
            }
        }
    }), [target, items, expanded]);
    useType(700, search => {
        if (isActive.current) {
            const re = new RegExp('^' + search, 'i');
            const found = files.map(item => item.name.match(re) ? item : null).find(isNotNil);
            found && click(found, false, false, false);
        }
    }, [files]);
    const dragStart = (i, e) => {
        onDragStart?.(selected.includes(items[i].path) ?
            selected.map(path => items.find(whereEq({ path })).URI) :
            [items[i].URI], e);
    };
    const dragCounter = useRef({ path: '', count: 0, timeout: undefined });
    const dragEnter = (item, e) => {
        const path = item.dir ? item.path : dirname(item.path);
        if (dragCounter.current.path == path) {
            dragCounter.current.count++;
        }
        else {
            clearTimeout(dragCounter.current.timeout);
            dragCounter.current = {
                path,
                count: 1,
                timeout: expanded.includes(path)
                    ? undefined
                    : setTimeout(() => toggle(path), 800)
            };
            setDropTarget(path);
        }
    };
    const dragLeave = (item, e) => {
        const path = item.dir ? item.path : dirname(item.path);
        if (dragCounter.current.path == path) {
            if (dragCounter.current.count > 1) {
                dragCounter.current.count--;
            }
            else {
                clearTimeout(dragCounter.current.timeout);
                dragCounter.current = { path: '', count: 0, timeout: undefined };
                setDropTarget('');
            }
        }
    };
    const dragDrop = (targetDir, e) => {
        e.preventDefault();
        e.stopPropagation();
        let URIs = [];
        const data = e.dataTransfer.getData('URIs');
        if (data && data.length) {
            URIs = JSON.parse(data);
        }
        if (URIs.length) {
            if (!URIs
                .map(URI => items.find(whereEq({ URI })))
                .filter(identity)
                .some(uri => uri && childOf(targetDir, uri.path))) {
                onDrop(URIs, targetDir, e.altKey ? DropEffect.Copy : DropEffect.Move, e);
            }
        }
        else if (e.dataTransfer.items.length) { // dropped from outside
            const files = [];
            for (let i = 0; i < e.dataTransfer.items.length; i++) {
                const item = e.dataTransfer.items[i];
                if (item.kind == 'file') {
                    const f = item.getAsFile();
                    f && files.push(f);
                }
            }
            files.length && onDrop(files, targetDir, DropEffect.Copy, e);
        }
        clearTimeout(dragCounter.current.timeout);
        dragCounter.current = { path: '', count: 0, timeout: undefined };
        setDropTarget('');
    };
    const dragEnd = (e) => {
        onDragEnd?.(e);
        setDropTarget('');
    };
    const resizing = useRef(null);
    const delayedOnChange = useCallback(onColumnsChange ? debounce(onColumnsChange, 500) : () => { }, [onColumnsChange]);
    useEvent(document, 'mousemove', e => {
        const { pageX } = e;
        if (resizing.current !== null && rootEl.current && columns.length) {
            const target = rootEl.current.querySelector(`th:nth-child(${resizing.current + 1})`);
            if (target) {
                const width = Math.max(50, Math.round(pageX - target.getBoundingClientRect().left));
                if (widths[resizing.current] != width) {
                    setWidths(update(resizing.current, width, widths));
                    delayedOnChange(columns.map(({ name }, i) => ({ name, width: widths[i] })));
                }
            }
        }
    });
    const draggingColumn = useRef(null);
    const [colDropTarget, setColDropTarget] = useState();
    const onColumnDragOver = (target) => () => {
        if (draggingColumn.current && target != draggingColumn.current) {
            setColDropTarget(target);
        }
    };
    const onColumnDrop = (target) => () => {
        if (draggingColumn.current && target != draggingColumn.current) {
            const newWidths = move(draggingColumn.current, target == widths.length - 1 ? target : target + 1, widths);
            onColumnsChange?.(move(draggingColumn.current, target == columns.length - 1 ? target : target + 1, columns)
                .map(({ name }, i) => ({ name, width: newWidths[i] })));
        }
        setColDropTarget(undefined);
    };
    useEvent(document, 'mouseup', () => {
        resizing.current = draggingColumn.current = null;
    });
    return (React.createElement("div", { className: classNames(styles.root, 'list'), ref: setRef(rootEl, fwdRef), onFocus: () => isActive.current = true, onBlur: () => isActive.current = false, onClick: e => { if (e.target == rootEl.current) {
            setTarget(undefined);
            setSelected([]);
        } }, onContextMenu: () => { setTarget(undefined); onMenu(root, true); }, onDragOver: e => onDragOver?.(e), onDrop: e => dragDrop(root, e), tabIndex: tabindex }, items.length > 0 || !children ?
        React.createElement(Tooltips, { delay: 800 },
            React.createElement("table", null,
                React.createElement("thead", null,
                    React.createElement("tr", { onContextMenu: e => { e.stopPropagation(); onColumnsMenu?.(); } },
                        columns.map(({ name, title, sort }, i) => React.createElement("th", { key: name, className: classNames({ sorted: !!sort, drop: i === colDropTarget }), style: { width: widths[i] }, "data-name": name, onClick: () => onSort?.(name), onMouseDown: () => i && (draggingColumn.current = i), onMouseOver: onColumnDragOver(i), onMouseOut: () => setColDropTarget(undefined), onMouseUp: onColumnDrop(i) },
                            title,
                            sort &&
                                React.createElement("span", { className: "icon" }, sort == SortOrder.Asc ? 'arrow_drop_up' : 'arrow_drop_down'),
                            React.createElement("i", { onMouseDown: e => { e.stopPropagation(); resizing.current = i; }, onClick: e => e.stopPropagation() }))),
                        React.createElement("th", null))),
                React.createElement("tbody", null,
                    parent &&
                        React.createElement("tr", { key: parent, className: 'up', onClick: () => onGo(parent), onContextMenu: e => { e.stopPropagation(); onMenu(parent, true); }, "data-tooltip": 'Go Up', "data-command": CommandID.GoUp },
                            React.createElement("td", { colSpan: columns.length + 1 },
                                React.createElement("span", { className: "icon" }, "keyboard_return"),
                                "..")),
                    items.map((item, i) => item.FileStateAttr == FileState.Creating || item.FileStateAttr == FileState.Renaming ?
                        React.createElement("tr", { key: 'editing' },
                            React.createElement("td", { colSpan: columns.length + 1, className: classNames({ d: item.dir }), "data-depth": depth(item.path) + 1 - rootDepth },
                                React.createElement(EditFileName, { name: item.name, sublings: creating
                                        ? selectChildren(creating.in, files.map(prop('path')))
                                        : [], onOk: nm => {
                                        creating
                                            ? onNew?.(nm, creating.in, creating.dir)
                                            : onRename?.(renaming, nm);
                                        createIn(undefined);
                                        rename(undefined);
                                    }, onCancel: () => { createIn(undefined); rename(undefined); } })),
                            React.createElement("td", null)) :
                        React.createElement("tr", { key: item.path, className: classNames({
                                selected: isSelected(item.path),
                                dragover: dropTarget && isDescendant(item.path, dropTarget),
                                target: target?.path == item.path
                            }), "data-path": item.path, onClick: ({ metaKey, shiftKey }) => click(item, metaKey, shiftKey, true), onDoubleClick: () => doubleClick(item), onContextMenu: e => { e.stopPropagation(); setTarget(item); onMenu(item.path, item.dir); }, draggable: true, onDragStart: e => dragStart(i, e), onDragEnd: dragEnd, onDragOver: e => onDragOver?.(e), onDragEnter: e => dragEnter(item, e), onDragLeave: e => dragLeave(item, e), onDrop: e => dragDrop(item.dir ? item.path : dirname(item.path), e) },
                            columns.map(({ name, type }, i) => i == 0 ?
                                React.createElement("td", { key: name, className: classNames({ d: item.dir, i: !!item.icon }), "data-depth": depth(item.path) - rootDepth, "data-f5-attributes": Object.entries(item[FileAttrsAttr] ?? {}).map(([id]) => id).join(' ') },
                                    React.createElement("div", { className: classNames({ expanded: expanded.includes(item.path) }) },
                                        item.dir &&
                                            React.createElement("i", { className: "icon" }, "arrow_forward_ios"),
                                        item.icon &&
                                            React.createElement("i", { className: "fileicon", "data-code": item.icon }),
                                        React.createElement("span", { "data-tooltip": item.tooltip ?? item.path }, item[name]),
                                        React.createElement("ul", null,
                                            item.target &&
                                                React.createElement("li", { className: "icon", "data-tooltip": 'Symbolic Link to ' + item.target }, "prompt_suggestion"),
                                            Object.entries(item[FileAttrsAttr] ?? {}).map(([id, name]) => React.createElement("li", { key: "id", "data-f5-attribute": id, "data-tooltip": name }))))) :
                                React.createElement("td", { key: name, className: 'type-' + type }, item[name])),
                            React.createElement("td", null)))))) :
        children));
});
